import {
  makeBridgeState,
  MakeBridgeStateParams,
} from "@ml-pa/react-apollo-form-tool/GenericBrigdeUtils/gerenericBridgeState";
import { GetCertificateToModifyByIdQuery } from "graphqlBase/certificates/__generated__/getCertificateToModifyById";
import { atom, selector, useRecoilCallback } from "recoil";
import { isExtendedValiditySubmitBlockedAtom } from "pages/shared/state/extendedValidityState";

export { isExtendedValiditySubmitBlockedAtom } from "pages/shared/state/extendedValidityState";
import { v4 as uuidv4 } from "uuid";
import { dummyIdPrefix, formDefaultKeys } from "../config";
import {
  CerticateUploadProcessType,
  FormValueGetter,
  RowJson,
  UploadCertificate,
  UploadCertificateFile,
} from "../types";
import { uploadCertificateFilesState } from "./uploadCertificateFileState";
import { activeUploadCertificateUnitIndexAtom, uploadCertificateUnitListAtom } from "./uploadCertificateUnitState";

import wait from "utils/wait";
import { getCertificateCreateFieldSelector } from "raft/CertificateForm";
import { userInfoAtom } from "lib/userInfo/state";

// Combined type for certificate replacement state
export type CertificateReplacementState =
  | {
      certificateId: string;
      operation: "replace" | "extend";
    }
  | undefined;
export const uploadCertificateStateAtom = atom<UploadCertificate | undefined>({
  key: "uploadCertificateStateAtom",
  default: undefined,
});

export const hasTG8ConformityInEditAtom = atom<boolean | undefined>({
  key: "hasTG8ConformityInEditAtom",
  default: false,
});

export const isUploadCertificateLoadingAtom = atom<boolean>({
  key: "isUploadCertificateLoadingAtom",
  default: false,
});
export const importedExcelOnExistingCertificate = atom<boolean>({
  key: "importedExcelOnExistingCertificate",
  default: false,
});

export const certicateUploadProcessTypeAtom = atom<CerticateUploadProcessType | undefined>({
  key: "certicateUploadProcessTypeAtom",
  default: undefined,
});
export const certificateToReplaceIdAtom = atom<string | undefined>({
  key: "certificateToReplaceIdAtom",
  default: undefined,
});

export const isUploadValidExtendedByAtom = atom<boolean>({
  key: "isUploadValidExtendedByAtom",
  default: false,
});

export const isCertificateToReplaceOperationAtom = atom<boolean>({
  key: "isCertificateToReplaceOperationAtom",
  default: false,
});

export const certificateToEditIdAtom = atom<string | undefined>({
  key: "certificateToEditIdAtom",
  default: undefined,
});

export const certificateToEditForNavigationAtom = atom<string | undefined>({
  key: "certificateToEditForNavigationAtom",
  default: undefined,
});

// Combined atom for certificate replacement (new approach)
export const certificateReplacementAtom = atom<CertificateReplacementState>({
  key: "certificateReplacementAtom",
  default: undefined,
});

// Helper selector to get combined replacement state
export const certificateReplacementStateSelector = selector({
  key: "certificateReplacementStateSelector",
  get: ({ get }) => {
    const certificateId = get(certificateToReplaceIdAtom);
    const isExtension = get(isUploadValidExtendedByAtom);

    if (!certificateId) return undefined;

    return {
      certificateId,
      operation: isExtension ? ("extend" as const) : ("replace" as const),
    };
  },
});

export const certificOnRemoteAtom = atom<GetCertificateToModifyByIdQuery["certificate"] | undefined>({
  key: "certificOnRemoteAtom",
  default: undefined,
});

export const isImportGoingToOverwriteSelector = selector<boolean>({
  key: "isImportGoingToOverwriteSelector",
  get: ({ get }) => {
    const uploadCertificateState = get(uploadCertificateStateAtom);
    const uploadCertificateUnitsState = get(uploadCertificateUnitListAtom);
    const keys = Object.keys(uploadCertificateUnitsState[0] || {}).filter((key) => !formDefaultKeys.includes(key));
    if (
      Object.keys(uploadCertificateState || {}).length > 0 ||
      uploadCertificateUnitsState.length > 1 ||
      keys.length > 0
    ) {
      return true;
    }
    return false;
  },
});

const makeBridgeAtomState = (ids: string[]) => {
  const v = ids.reduce((acc, id) => {
    acc[id] = { outerId: id, isDeleted: false };
    return acc;
  }, {} as { [key: string]: { outerId: string; isDeleted: boolean } });
  return v;
};

export const certificateBridgeCertificateNormIssueDatesState = makeBridgeState({
  bridgeParams: { bridgeEntityName: "certificateBridgeCertificateNormIssueDates" },
} as MakeBridgeStateParams);

export const certificateBridgeSoftwareVersionState = makeBridgeState({
  bridgeParams: { bridgeEntityName: "certificateBridgeSoftwareVersions" },
} as MakeBridgeStateParams);

export const useImportValuesToCertificateUploadForm = (getFormValues: FormValueGetter, isEdit = false) => {
  return useRecoilCallback(
    ({ set, reset, snapshot }) =>
      async (columns: RowJson[][]) => {
        const userInfo = await snapshot.getPromise(userInfoAtom);
        const role = userInfo?.role;
        if (!role) return;

        set(isUploadCertificateLoadingAtom, true);
        const formValues = await getFormValues({ columns });
        if (!formValues) {
          set(isUploadCertificateLoadingAtom, false);
          return;
        }
        const {
          certificateUpload,
          certificateUploadUnits: importedUnits,
          certificateBridgeCertificateNormIssueDates,
          certificateBridgeSoftwareVersions,
        } = formValues;

        const certificateUploadForRole = {
          ...certificateUpload,
          certificateHolderId: role === "MANUFACTURER" ? userInfo.tenantId : certificateUpload.certificateHolderId,
          certificateAuthorityId:
            role === "CERTIFICATION_AUTHORITY" ? userInfo.tenantId : certificateUpload.certificateAuthorityId,
        };

        // --- Merge logic for extension flow ---
        // If this is an extension flow, merge imported units with existing units (UI only)
        const replacementState = await snapshot.getPromise(certificateReplacementStateSelector);
        let finalUnits = importedUnits;
        if (replacementState?.operation === "extend") {
          const existingUnits = await snapshot.getPromise(uploadCertificateUnitListAtom);
          // Merge by modelName (or id if needed), keeping all unique units
          const existingUnitsMap = new Map(existingUnits.map((u) => [u.modelName || u.id, u]));
          importedUnits.forEach((u) => {
            existingUnitsMap.set(u.modelName || u.id, u);
          });
          finalUnits = Array.from(existingUnitsMap.values());
        }
        // --- End merge logic ---

        set(activeUploadCertificateUnitIndexAtom, -1);
        reset(uploadCertificateUnitListAtom);
        if (!isEdit) {
          reset(uploadCertificateStateAtom);
          reset(certificateBridgeCertificateNormIssueDatesState.state);
          reset(certificateBridgeSoftwareVersionState.state);

          set(uploadCertificateStateAtom, certificateUploadForRole);

          await wait(500);
          set(
            certificateBridgeCertificateNormIssueDatesState.state,
            makeBridgeAtomState(certificateBridgeCertificateNormIssueDates)
          );
          await wait(500);
          set(certificateBridgeSoftwareVersionState.state, makeBridgeAtomState(certificateBridgeSoftwareVersions));
        } else {
          set(importedExcelOnExistingCertificate, true);
        }
        set(uploadCertificateUnitListAtom, finalUnits);
        await wait(50);
        set(activeUploadCertificateUnitIndexAtom, 0);
        set(isUploadCertificateLoadingAtom, false);
      },
    []
  );
};

export const useSetValuesForCertificateToModify = (isEdit?: boolean, isUploadValidExtendedBy?: boolean) => {
  const __modifiedByUserAtom = getCertificateCreateFieldSelector({ formVersion: "active" })("__modifiedByUser");
  return useRecoilCallback(
    ({ set, reset }) =>
      async (toModify: GetCertificateToModifyByIdQuery) => {
        if (!toModify.certificate) {
          set(isUploadCertificateLoadingAtom, false);
          return;
        }
        set(certificOnRemoteAtom, toModify.certificate);
        const {
          certificateBridgeCertificateNormIssueDates,
          certificateBridgeSoftwareVersions,
          certificateBridgeUnits,
          revision,
          ...certificateUploadPre
        } = toModify.certificate;
        if (certificateUploadPre.withTG8Rev25Conformity === true) {
          set(hasTG8ConformityInEditAtom, true);
        }

        const nextRevision = (revision ?? 0) + 1;
        const certificateUpload: UploadCertificate = {
          ...certificateUploadPre,
          id: undefined,
          __certificateToReplaceId: isEdit ? undefined : toModify.certificate.id,
          revision: isEdit ? revision : nextRevision,
          isVerified: false,
          __modifiedByUser: 0,
          certificateValidityStatusId: isEdit
            ? certificateUploadPre.certificateValidityStatusId
            : isUploadValidExtendedBy
            ? "c47a0b7c-701b-5544-92b0-6bcb48aac633" // valid
            : certificateUploadPre.certificateValidityStatusId,
        };

        const certificateUploadUnits =
          certificateBridgeUnits.map(({ unit }, index) => {
            // Exclude __typename, unitType, and grant access array fields (incompatible with RAFT form types)
            const { __typename, unitType, grantAccessToUnitParametersTenantIds, ...rest } = unit;
            // Use real ID for edit or extension mode, dummy only for new units
            const shouldUseRealId = isEdit || isUploadValidExtendedBy;
            const assignedId = shouldUseRealId ? rest.id : `${dummyIdPrefix}${uuidv4()}`;
            return {
              ...rest,
              id: assignedId,
              __unitToReplaceId: unit.id,
              __matrixField: "", // Matrix parameters will be loaded separately when needed
              __isLinked: undefined,
              revision: nextRevision,
            };
          }) ?? [];

        const certificateBridgeSoftwareVersionsValues = certificateBridgeSoftwareVersions.map(
          ({ softwareVersionId }) => softwareVersionId
        );

        const certificateBridgeCertificateNormIssueDatesValues = certificateBridgeCertificateNormIssueDates.map(
          ({ certificateNormIssueDate }) => certificateNormIssueDate.id
        );

        set(activeUploadCertificateUnitIndexAtom, -1);
        reset(uploadCertificateUnitListAtom);
        reset(uploadCertificateStateAtom);
        reset(certificateBridgeCertificateNormIssueDatesState.state);
        reset(certificateBridgeSoftwareVersionState.state);

        set(uploadCertificateStateAtom, certificateUpload);
        set(uploadCertificateUnitListAtom, certificateUploadUnits);

        await wait(50);
        set(activeUploadCertificateUnitIndexAtom, 0);

        await wait(500);
        set(
          certificateBridgeCertificateNormIssueDatesState.state,
          makeBridgeAtomState(certificateBridgeCertificateNormIssueDatesValues)
        );
        await wait(500);
        set(certificateBridgeSoftwareVersionState.state, makeBridgeAtomState(certificateBridgeSoftwareVersionsValues));
        if (isEdit) {
          const exitistingFiles = (toModify.certificate.certificateFiles ?? []).reduce<UploadCertificateFile[]>(
            (files, file) => {
              if (!file) return files;
              return files.concat({
                ...file,
                base64FileString: "",
                certificateId: toModify.certificate?.id ?? "",
                size: 0,
              });
              return files;
            },
            []
          );
          await wait(300);
          set(uploadCertificateFilesState, exitistingFiles);
          await wait(300);
          set(__modifiedByUserAtom, 1);
        }

        set(isUploadCertificateLoadingAtom, false);
      },
    []
  );
};

export const useResetForm = () => {
  return useRecoilCallback(({ reset }) => () => {
    reset(uploadCertificateUnitListAtom);
    reset(uploadCertificateStateAtom);
    reset(certificateBridgeCertificateNormIssueDatesState.state);
    reset(certificateBridgeSoftwareVersionState.state);
    reset(activeUploadCertificateUnitIndexAtom);
    reset(uploadCertificateFilesState);
    reset(uploadCertificateUnitListAtom);
    reset(certicateUploadProcessTypeAtom);
    reset(isUploadCertificateLoadingAtom);
    reset(isExtendedValiditySubmitBlockedAtom);
  });
};
